import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Mail, Phone, MapPin, Send, Calendar } from 'lucide-react';
import { projectTypes } from '../data/mock';
import { toast } from 'sonner';
import { AppointmentCalendar } from './AppointmentCalendar';
import { Logo3DAnimated } from './Logo3DAnimated';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = ({ formOnly = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (value) => {
    setFormData({
      ...formData,
      projectType: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          project_type: formData.projectType,
          message: formData.message
        })
      });

      if (response.ok) {
        toast.success('Message envoyé avec succès ! Nous vous contacterons bientôt.');
        setFormData({ name: '', email: '', projectType: '', message: '' });
      } else {
        toast.error("Une erreur s'est produite. Veuillez réessayer.");
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error("Impossible d'envoyer le message. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-dark relative overflow-hidden">
      {/* Logo 3D en arrière-plan avec effet */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-10 pointer-events-none">
        <Logo3DAnimated size="large" />
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          {/* Logo 3D visible en haut */}
          <div className="flex justify-center mb-8">
            <Logo3DAnimated size="normal" />
          </div>
          
          <div className="inline-block mb-4">
            <div className="h-[1px] w-12 bg-gold mx-auto"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-white mb-6">
            Démarrons Votre Projet
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Partagez-nous votre vision, nous la transformerons en réalité
          </p>
        </div>
        
        <div className="max-w-5xl mx-auto">
          <Tabs defaultValue="contact" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-white/5 border border-gold/20 mb-12">
              <TabsTrigger 
                value="contact"
                className="data-[state=active]:bg-gold data-[state=active]:text-dark text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                Nous Contacter
              </TabsTrigger>
              <TabsTrigger 
                value="appointment"
                className="data-[state=active]:bg-gold data-[state=active]:text-dark text-white"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Prendre Rendez-vous
              </TabsTrigger>
            </TabsList>

            <TabsContent value="contact">
              <div className={formOnly ? 'max-w-2xl mx-auto' : 'grid grid-cols-1 lg:grid-cols-2 gap-12'}>
          <div className={formOnly ? 'hidden' : 'space-y-8'}>
            <div>
              <h3 className="text-2xl font-playfair font-bold text-white mb-6">
                Parlons de votre projet
              </h3>
              <p className="text-gray-400 leading-relaxed mb-8">
                Que vous ayez un projet résidentiel ou professionnel, notre équipe est à votre écoute 
                pour transformer vos espaces avec précision et élégance.
              </p>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gold/10 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors duration-300">
                  <Mail className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Email</p>
                  <a href="mailto:tommy@jenmaconcept.com" className="text-white hover:text-gold transition-colors duration-300">
                    tommy@jenmaconcept.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gold/10 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors duration-300">
                  <Phone className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Téléphone</p>
                  <a href="tel:+33695767100" className="text-white hover:text-gold transition-colors duration-300">
                    06 95 76 71 00
                  </a>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 group">
                <div className="w-12 h-12 bg-gold/10 rounded-sm flex items-center justify-center flex-shrink-0 group-hover:bg-gold/20 transition-colors duration-300">
                  <MapPin className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Zone d'intervention</p>
                  <p className="text-white" data-testid="contact-hq">Siège : Salon-de-Provence (13300)</p>
                  <p className="text-gray-400 text-sm">Intervention : Provence-Alpes-Côte d'Azur · Paris</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-light/5 backdrop-blur-sm p-8 rounded-sm border border-gold/20 shadow-luxury">
            <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-white mb-2 block">Nom complet</Label>
                <Input
                  id="name"
                  data-testid="contact-name-input"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-white/5 border-gold/30 text-white focus:border-gold focus:ring-gold"
                  placeholder="Votre nom"
                />
              </div>
              
              <div>
                <Label htmlFor="email" className="text-white mb-2 block">Email</Label>
                <Input
                  id="email"
                  data-testid="contact-email-input"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white/5 border-gold/30 text-white focus:border-gold focus:ring-gold"
                  placeholder="votre@email.fr"
                />
              </div>
              
              <div>
                <Label htmlFor="projectType" className="text-white mb-2 block">Type de projet</Label>
                <Select value={formData.projectType} onValueChange={handleSelectChange}>
                  <SelectTrigger data-testid="contact-project-type-select" className="bg-white/5 border-gold/30 text-white focus:border-gold focus:ring-gold">
                    <SelectValue placeholder="Sélectionnez un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {projectTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="message" className="text-white mb-2 block">Message</Label>
                <Textarea
                  id="message"
                  data-testid="contact-message-input"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-white/5 border-gold/30 text-white focus:border-gold focus:ring-gold resize-none"
                  placeholder="Décrivez votre projet..."
                />
              </div>
              
              <Button
                type="submit"
                data-testid="contact-submit-button"
                disabled={isSubmitting}
                className="w-full bg-gold hover:bg-gold-light text-dark font-semibold py-6 transition-all duration-300 shadow-gold group"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </form>
              </div>
            </div>
            </TabsContent>

            <TabsContent value="appointment">
              <div className="max-w-2xl mx-auto">
                <AppointmentCalendar />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
};
